import { useMemo, useState } from "react";
import { Bookmark, BookmarkCheck, Hash, Loader2, MessageSquare, Search, Trash2, UsersRound } from "lucide-react";
import { useSavedMessages, useUnsaveMessage } from "@/api/hooks";
import type { SavedMessageSummary } from "@/api/connectApi";
import { cn } from "@/lib/utils";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

/**
 * Teams-style "Saved" surface — chronological list of every message
 * the user has bookmarked across channels, DMs, and group
 * conversations. Each row shows the author + body snapshot taken at
 * save time (so an edit/delete on the source doesn't break the
 * saved entry) plus a quick unsave action.
 */
export function SavedMessagesView({
    onJump
}: {
    onJump?: (saved: SavedMessageSummary) => void;
}) {
    const saved = useSavedMessages();
    const unsave = useUnsaveMessage();
    const [filter, setFilter] = useState("");
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [bulkRemoving, setBulkRemoving] = useState(false);

    const toggleSelected = (id: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleBulkRemove = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        if (!window.confirm(`Remove ${ids.length} saved message${ids.length === 1 ? "" : "s"}?`)) return;
        setBulkRemoving(true);
        try {
            for (const id of ids) {
                await unsave.mutateAsync(id);
            }
            setSelected(new Set());
        } finally {
            setBulkRemoving(false);
        }
    };

    const allItems = saved.data ?? [];
    const visibleItems = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (q.length === 0) return allItems;
        return allItems.filter((s) =>
            (s.authorDisplayName ?? "").toLowerCase().includes(q) ||
            s.authorEmail.toLowerCase().includes(q) ||
            // Body is HTML-fragment; strip tags cheaply for matching only.
            // The dangerously-set HTML still renders unchanged.
            stripHtmlForMatch(s.bodyHtml).includes(q)
        );
    }, [allItems, filter]);

    const grouped = useMemo(() => ({
        channel: visibleItems.filter((s) => s.containerKind === "channel"),
        dm: visibleItems.filter((s) => s.containerKind === "dm"),
        conversation: visibleItems.filter((s) => s.containerKind === "conversation"),
        total: visibleItems.length
    }), [visibleItems]);

    return (
        <main className="min-h-0 flex-1 overflow-auto bg-background">
            <div className="mx-auto max-w-3xl p-6">
                <header className="mb-4 flex items-center gap-2">
                    <Bookmark className="h-5 w-5 text-primary" aria-hidden />
                    <h1 className="text-lg font-semibold">Saved</h1>
                    {selected.size > 0 ? (
                        <button
                            type="button"
                            onClick={() => void handleBulkRemove()}
                            disabled={bulkRemoving}
                            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-destructive/40 px-2 py-1 text-xs text-destructive hover:bg-destructive hover:text-destructive-foreground disabled:opacity-50"
                        >
                            {bulkRemoving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                            Remove {selected.size}
                        </button>
                    ) : (
                        <span className="ml-auto text-xs text-muted-foreground">
                            {grouped.total} saved
                        </span>
                    )}
                </header>

                {allItems.length > 0 ? (
                    <div className="relative mb-3 max-w-sm">
                        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                        <input
                            type="search"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filter saved by author or text"
                            aria-label="Filter saved messages"
                            className="h-8 w-full rounded-md border bg-background pl-7 pr-2 text-sm outline-none focus:border-primary"
                        />
                    </div>
                ) : null}

                {saved.isPending ? (
                    <p className="text-sm text-muted-foreground">Loading…</p>
                ) : allItems.length === 0 ? (
                    <div className="rounded-lg border bg-card p-10 text-center text-sm text-muted-foreground">
                        <Bookmark className="mx-auto mb-2 h-6 w-6" aria-hidden />
                        <p>No saved messages yet.</p>
                        <p className="text-xs">
                            Hover any message and click the bookmark icon to keep it for later.
                        </p>
                    </div>
                ) : grouped.total === 0 ? (
                    <p className="text-sm text-muted-foreground">No saved messages match &ldquo;{filter}&rdquo;.</p>
                ) : (
                    <ul role="list" className="flex flex-col gap-2">
                        {visibleItems.map((s) => (
                            <SavedRow
                                key={s.id}
                                saved={s}
                                onJump={onJump}
                                onRemove={() => unsave.mutate(s.id)}
                                checked={selected.has(s.id)}
                                onToggleSelect={() => toggleSelected(s.id)}
                            />
                        ))}
                    </ul>
                )}
            </div>
        </main>
    );
}

function SavedRow({
    saved,
    onJump,
    onRemove,
    checked,
    onToggleSelect
}: {
    saved: SavedMessageSummary;
    onJump?: (saved: SavedMessageSummary) => void;
    onRemove: () => void;
    checked: boolean;
    onToggleSelect: () => void;
}) {
    return (
        <li
            className={cn(
                "rounded-lg border bg-card p-3 text-sm",
                onJump ? "hover:border-primary/40" : null,
                checked ? "border-primary/60 bg-primary/5" : null
            )}
        >
            <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={onToggleSelect}
                    aria-label={checked ? "Deselect saved message" : "Select saved message"}
                    className="h-3.5 w-3.5 cursor-pointer"
                />
                <KindIcon kind={saved.containerKind} />
                <span>{kindLabel(saved.containerKind)}</span>
                <span className="ml-auto">{formatRelative(saved.savedAt)}</span>
            </div>
            <button
                type="button"
                onClick={() => onJump?.(saved)}
                disabled={!onJump}
                className="block w-full text-left"
            >
                <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium">
                        {saved.authorDisplayName || saved.authorEmail}
                    </span>
                    <span className="text-xs text-muted-foreground">
                        {formatTime(saved.sentAt)}
                    </span>
                </div>
                <div
                    className="prose prose-sm mt-1 max-w-none break-words text-foreground dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(saved.bodyHtml) }}
                />
            </button>
            <div className="mt-2 flex justify-end">
                <button
                    type="button"
                    onClick={onRemove}
                    aria-label="Unsave"
                    title="Remove from Saved"
                    className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-accent"
                >
                    <BookmarkCheck className="h-3.5 w-3.5 text-primary" />
                    Saved · click to remove
                </button>
            </div>
        </li>
    );
}

function KindIcon({ kind }: { kind: SavedMessageSummary["containerKind"] }) {
    if (kind === "channel") return <Hash className="h-3.5 w-3.5" aria-hidden />;
    if (kind === "dm") return <MessageSquare className="h-3.5 w-3.5" aria-hidden />;
    return <UsersRound className="h-3.5 w-3.5" aria-hidden />;
}

function kindLabel(kind: SavedMessageSummary["containerKind"]): string {
    if (kind === "channel") return "Channel";
    if (kind === "dm") return "Direct chat";
    return "Group chat";
}

/**
 * Strip HTML tags + decode the most common entities so a search like
 * "approved" matches "&lt;p&gt;Approved!&lt;/p&gt;". Cheap regex-based —
 * we don't need a full parser for "does this substring appear" matching.
 */
function stripHtmlForMatch(html: string): string {
    return html
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/&quot;/gi, "\"")
        .replace(/&#39;/g, "'")
        .toLowerCase();
}

function formatTime(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit"
    });
}

function formatRelative(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const diff = Date.now() - d.getTime();
    const m = Math.floor(diff / 60_000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
